import { Route, Switch, useParams } from 'react-router-dom';
import { sidebarPath } from '@features/survey-settings-toolbar/breadcrumbPath';
import { TemplateHistoryConfiguration } from './template-history/TemplateHistoryConfiguration';
import { TemplateBulkSurveysCreate } from '@features/survey-templates/TemplateBulkSurveysCreate';
import { TemplateConfiguration } from '@features/survey-templates/TemplateConfiguration';
import {
  TemplateContainer,
  TemplateContext,
  TemplateContextLoadedProps,
} from '@features/survey-templates/TemplateContainer';
import { TemplateDashboard } from '@features/survey-templates/TemplateDashboard';
import { TemplateHistory } from './template-history/TemplateHistory';
import { TemplateScope } from '@features/survey-templates/TemplateScope';
import { NavCode } from '@features/survey-templates/types';
import { rootAppPath } from '../../routes/company-tracker/utils';
import { TemplateNav } from '@features/survey-templates/partials/TemplateNav';
import { TemplateHistoryNav } from './template-history/TemplateHistoryNav';
import { useContext } from 'react';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { TemplateHistorySubsidiary } from './template-history/TemplateHistorySubsidiary';
import { TemplateHistoryHeader } from './template-history/TemplateHistoryHeader';
import { useAppSelector } from '../../reducers';
import { isUserManagerByInitiativeId } from '../../selectors/user';
import NotAuthorised from '../not-authorised';
import { AggregatedConfiguration } from '@features/survey-templates/aggregated/AggregatedConfiguration';
import { AggregatedBulkSurveysCreate } from '@features/survey-templates/aggregated/AggregatedBulkSurveysCreate';
import { TemplateHistoryAggregatedConfiguration } from './template-history/TemplateHistoryAggregatedConfiguration';
import { SURVEY } from '@constants/terminology';

const TemplateSettingPages = () => {
  const { page } = useParams<{ page?: string }>();
  const { templateData } = useContext(TemplateContext) as TemplateContextLoadedProps;
  const isAggregated = templateData.type === SurveyType.Aggregation;

  switch (page) {
    case NavCode.Configuration:
      return isAggregated ? <AggregatedConfiguration /> : <TemplateConfiguration />;
    case NavCode.Scope:
      return isAggregated ? <TemplateDashboard /> : <TemplateScope renderHeader={() => <TemplateNav />} />;
    case NavCode.BulkSurveysCreate:
      return isAggregated ? <AggregatedBulkSurveysCreate /> : <TemplateBulkSurveysCreate />;
    case NavCode.History:
      return <TemplateHistory />;
    default:
      return <TemplateDashboard />;
  }
};

const HistorySettingPages = () => {
  const { page } = useParams<{
    initiativeId: string;
    templateId: string;
    historyId: string;
    page?: string;
  }>();
  const templateContext = useContext(TemplateContext) as TemplateContextLoadedProps;
  const isAggregated = templateContext.templateData.type === SurveyType.Aggregation;

  switch (page) {
    case NavCode.Configuration:
      return isAggregated ? <TemplateHistoryAggregatedConfiguration /> : <TemplateHistoryConfiguration />;
    case NavCode.Scope:
      return (
        <TemplateScope
          isDisabled
          renderBackButton={() => <TemplateHistoryHeader />}
          renderHeader={() => <TemplateHistoryNav />}
          title={`${SURVEY.CAPITALIZED_SINGULAR} templates: ${templateContext.templateData.name}`}
        />
      );
    case NavCode.Subsidiary:
      return <TemplateHistorySubsidiary />;
    default:
      return <TemplateHistory />;
  }
};

export const SurveyTemplatesRoute = () => {
  const { initiativeId } = useParams<{
    initiativeId: string;
  }>();
  const isManager = useAppSelector((state) => isUserManagerByInitiativeId(state, initiativeId));
  if (!isManager) {
    return <NotAuthorised />;
  }

  return (
    <Switch>
      <Route path={`/${rootAppPath}/survey-templates/:initiativeId/template/create`}>
        <TemplateConfiguration />
      </Route>
      <Route path={`/${rootAppPath}/survey-templates/:initiativeId/aggregated/create`}>
        <AggregatedConfiguration />
      </Route>
      <Route
        path={`/${rootAppPath}/survey-templates/:initiativeId/template/:templateId/history/:historyId/:page?/${sidebarPath}`}
      >
        <TemplateContainer>
          <HistorySettingPages />
        </TemplateContainer>
      </Route>
      <Route path={`/${rootAppPath}/survey-templates/:initiativeId/template/:templateId/:page?/${sidebarPath}`}>
        <TemplateContainer>
          <TemplateSettingPages />
        </TemplateContainer>
      </Route>
      <Route>
        <TemplateDashboard />
      </Route>
    </Switch>
  );
};
